/**
 * Operator Settings panel — the tenant's OWN **Abbonamento** (ADR-0041 §11): current plan,
 * usage meters, and a plan comparison. Auth-only read; the SELF-SERVE plan change is gated
 * `settings:write` (pre-Stripe — no payment; Stripe Checkout is Fase 1). Switching a plan
 * refreshes both the overview AND the entitlements provider (so nav gating updates live).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { BillingOverviewView } from "@/components/billing/BillingOverviewView";
import { type ChangePlanState, ChangePlanDialog } from "@/components/billing/ChangePlanDialog";
import { OrgWorkspaces } from "@/components/settings/OrgWorkspaces";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { type BillingChangePreview, type BillingPeriod, billingApi } from "@/lib/api-billing";
import { usePermissions } from "@/lib/permissions";

const EMPTY_PREVIEW: Omit<ChangePlanState, "planName"> = {
  loading: false,
  blocked: false,
  error: false,
  preview: null,
};

export function BillingSection() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const qc = useQueryClient();
  const canChange = can("settings", "write");
  // The pending switch (plan key + cadence + name) drives the confirm dialog; `prev` holds the
  // resolved preview state (loading → Stripe amounts / free / blocked / error).
  const [confirm, setConfirm] = useState<{
    planKey: string;
    period: BillingPeriod;
    planName: string;
  } | null>(null);
  const [prev, setPrev] = useState<Omit<ChangePlanState, "planName">>(EMPTY_PREVIEW);

  const q = useQuery({ queryKey: ["billing-overview"], queryFn: () => billingApi.overview() });
  const plan = q.data?.plan ?? null;
  const billed = !!plan && !q.data?.unlimited;
  const scheduledCancel = !!plan?.cancel_at_period_end;
  // Stripe ON + a NON-Stripe sub = a plan the provider assigned MANUALLY: the Stripe portal
  // does not exist for it and the self-serve switch is refused server-side (the anti
  // free-upgrade guard) — offering those buttons is a dead end, so hide them and say so.
  const manualManaged = billed && !!q.data?.stripe_enabled && !q.data?.stripe_managed;
  // A DOWNGRADE scheduled at period end (owner policy): show the "passerai a X il …" banner
  // + an Annulla cambio button. The target plan's NAME is resolved from the catalog.
  const pendingKey = plan?.pending_plan_key ?? null;
  const pendingPlanName = pendingKey
    ? (q.data?.plans.find((p) => p.key === pendingKey)?.name ?? pendingKey)
    : null;

  const cancelPending = useMutation({
    mutationFn: () => billingApi.cancelPendingChange(),
    onSuccess: (overview) => {
      qc.setQueryData(["billing-overview"], overview);
      void qc.invalidateQueries({ queryKey: ["entitlements"] });
    },
  });

  // Self-serve cancel/resume (ADR-0041 §13) — the native in-Tray twin of the Stripe Customer
  // Portal cancel. cancel=true SCHEDULES the churn (run to period end, then suspend; NEVER
  // Unlimited); cancel=false resumes. Refreshes the overview + entitlements (nav gating).
  const cancel = useMutation({
    mutationFn: (doCancel: boolean) => billingApi.cancelSubscription(doCancel),
    onSuccess: (overview) => {
      qc.setQueryData(["billing-overview"], overview);
      void qc.invalidateQueries({ queryKey: ["entitlements"] });
    },
  });

  const onCancel = () => {
    if (
      window.confirm(
        t(
          "billing.cancelConfirm",
          "Annullare l'abbonamento? Resta attivo fino alla fine del periodo, poi viene sospeso. NON diventa illimitato.",
        ),
      )
    )
      cancel.mutate(true);
  };

  // Attach a card to a manually-assigned plan (v0.1.20) — the way OUT of "gestito dal
  // fornitore". It does NOT change the subscription here: it redirects to Stripe Checkout
  // and the webhook links the sub once the payment lands.
  const activateCard = useMutation({
    mutationFn: () => billingApi.activateCard(),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });

  // Stripe Customer Portal (ADR-0041 Fase 1) — manage card / cancel / upgrade. The button
  // shows only for a billed tenant (has a plan); a comp / pre-Stripe tenant 422s, surfaced
  // as a one-line error rather than a redirect.
  const portal = useMutation({
    mutationFn: () => billingApi.portal(),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });

  // STEP 1 — Stripe computes the proration + VAT (Tray does no math). A 422
  // `billing:no_stripe_subscription` means the tenant is billed but unmanaged → blocked.
  const preview = useMutation({
    mutationFn: ({ planKey, period }: { planKey: string; period: BillingPeriod }) =>
      billingApi.previewChangePlan(planKey, period),
    onMutate: () => setPrev({ loading: true, blocked: false, error: false, preview: null }),
    onSuccess: (data: BillingChangePreview) =>
      setPrev({ loading: false, blocked: false, error: false, preview: data }),
    onError: (e) => {
      const blocked = e instanceof ApiError && e.message.includes("no_stripe_subscription");
      setPrev({ loading: false, blocked, error: !blocked, preview: null });
    },
  });

  // STEP 2 — apply, echoing the preview's `proration_date` so the charge == the confirmed total.
  const change = useMutation({
    mutationFn: ({ planKey, period }: { planKey: string; period: BillingPeriod }) =>
      billingApi.changePlan(planKey, period, prev.preview?.proration_date),
    onSuccess: (overview) => {
      qc.setQueryData(["billing-overview"], overview);
      void qc.invalidateQueries({ queryKey: ["entitlements"] }); // nav gating follows
      setConfirm(null);
    },
  });

  const onSelectPlan = (planKey: string, period: BillingPeriod) => {
    const plan = q.data?.plans.find((p) => p.key === planKey);
    setConfirm({ planKey, period, planName: plan?.name ?? planKey });
    preview.mutate({ planKey, period });
  };

  const closeConfirm = () => {
    if (change.isPending) return; // don't drop a mid-charge dialog
    setConfirm(null);
    setPrev(EMPTY_PREVIEW);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            {t("settings.nav.billing", "Abbonamento")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("billing.subtitle", "Piano attuale, consumi e funzionalità incluse.")}
          </p>
        </div>
        {canChange && q.data && !q.data.unlimited && (
          <div className="flex items-center gap-2">
            {billed && !scheduledCancel && (
              <Button
                variant="outline"
                size="sm"
                disabled={cancel.isPending}
                onClick={onCancel}
                className="border-danger/40 text-danger hover:bg-danger/10"
              >
                {t("billing.cancelSub", "Annulla abbonamento")}
              </Button>
            )}
            {!manualManaged && (
              <Button
                variant="outline"
                size="sm"
                disabled={portal.isPending}
                onClick={() => portal.mutate()}
              >
                {portal.isPending
                  ? t("common.loading", "Caricamento…")
                  : t("billing.managePortal", "Gestisci abbonamento")}
              </Button>
            )}
          </div>
        )}
      </div>
      {/* Manually-assigned plan (no Stripe sub): no portal, no self-serve switch — the
          provider manages it. But it's no longer a dead end: "Attiva pagamento con carta"
          opens a Stripe Checkout for the plan they're ALREADY on, and the webhook links the
          resulting subscription (they land in the normal self-serve flow from there). */}
      {manualManaged && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
          <p className="text-sm text-muted-foreground">
            {t(
              "billing.managedByProvider",
              "Il tuo piano è gestito dal fornitore. Attiva il pagamento con carta per gestirlo da qui, oppure contatta il supporto.",
            )}
          </p>
          {canChange && (
            <Button
              size="sm"
              disabled={activateCard.isPending}
              onClick={() => activateCard.mutate()}
            >
              {activateCard.isPending
                ? t("common.loading", "Caricamento…")
                : t("billing.activateCard", "Attiva pagamento con carta")}
            </Button>
          )}
        </div>
      )}
      {activateCard.isError && (
        <p className="text-sm text-danger">
          {t("billing.activateCardFailed", "Impossibile aprire il pagamento")}
          {": "}
          {activateCard.error instanceof Error ? activateCard.error.message : ""}
        </p>
      )}
      {/* Scheduled-cancellation banner — the sub keeps working until the period end, then suspends
          (it does NOT become Unlimited). "Riprendi" undoes it. */}
      {scheduledCancel && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-danger/30 bg-danger/5 px-4 py-3">
          <p className="text-sm">
            <span className="font-medium text-danger">
              {t("billing.cancelScheduled", "Abbonamento in cancellazione")}
            </span>
            <span className="text-muted-foreground">
              {" · "}
              {plan?.current_period_end
                ? `${t("billing.activeUntil", "resta attivo fino al")} ${new Date(
                    plan.current_period_end,
                  ).toLocaleDateString()}, ${t("billing.thenSuspended", "poi viene sospeso")}`
                : t(
                    "billing.thenSuspendedNoDate",
                    "resta attivo fino a fine periodo, poi viene sospeso",
                  )}
            </span>
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate(false)}
          >
            {t("billing.resumeSub", "Riprendi abbonamento")}
          </Button>
        </div>
      )}
      {/* Scheduled DOWNGRADE banner (owner policy: downgrades apply at period end — the paid
          plan runs to its natural expiry). "Annulla cambio" releases the schedule. */}
      {pendingKey && !scheduledCancel && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/40 bg-warning/5 px-4 py-3">
          <p className="text-sm">
            <span className="font-medium">
              {t("billing.pendingChange", "Cambio piano programmato")}
            </span>
            <span className="text-muted-foreground">
              {" · "}
              {plan?.pending_change_at
                ? t("billing.pendingChangeDetail", "passerai a {{plan}} il {{date}}", {
                    plan: pendingPlanName,
                    date: new Date(plan.pending_change_at).toLocaleDateString(),
                  })
                : t("billing.pendingChangeDetailNoDate", "passerai a {{plan}} a fine periodo", {
                    plan: pendingPlanName,
                  })}
            </span>
          </p>
          {canChange && (
            <Button
              variant="outline"
              size="sm"
              disabled={cancelPending.isPending}
              onClick={() => cancelPending.mutate()}
            >
              {t("billing.cancelPendingChange", "Annulla cambio")}
            </Button>
          )}
        </div>
      )}
      {(portal.isError || cancel.isError || cancelPending.isError) && (
        <p className="text-sm text-danger">
          {portal.isError
            ? t("billing.portalError", "Portale di fatturazione non disponibile.")
            : t("billing.cancelError", "Impossibile aggiornare l'abbonamento.")}
        </p>
      )}
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading", "Caricamento…")}</p>
      ) : q.data ? (
        <BillingOverviewView
          data={q.data}
          onSelectPlan={canChange && !manualManaged ? onSelectPlan : undefined}
          changingKey={change.isPending ? (confirm?.planKey ?? null) : null}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          {t("billing.loadError", "Impossibile caricare i dati di abbonamento.")}
        </p>
      )}
      {/* The company's OTHER workspaces (ADR-0076 fetta 5) — a separate permission and a
          separate principal (the caller's identity, not the Organizzazione), so it's its own card
          rather than a section of the plan view. */}
      <OrgWorkspaces />
      <ChangePlanDialog
        open={confirm != null}
        state={{ planName: confirm?.planName ?? "", ...prev }}
        busy={change.isPending}
        onConfirm={() =>
          confirm && change.mutate({ planKey: confirm.planKey, period: confirm.period })
        }
        onClose={closeConfirm}
      />
    </div>
  );
}
